import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Snowflake,
  Fire,
  Wind,
  Wrench,
  ArrowRight,
  CheckCircle,
  Lightning,
  ShieldCheck,
  ChatCircle,
  Images,
} from '@phosphor-icons/react';
import { JsonLdOrganization } from '../components/JsonLdOrganization';
import { PrimarySectionBackdrop } from '../components/PrimarySectionBackdrop';
import { Seo } from '../components/Seo';
import { KONTAKT_FORM_PATH } from '../constants/navigation';
import { SEO } from '../constants/seoCopy';
import { LOGO_SRC } from '../constants/branding';

const IMAGES = {
  hero: `${process.env.PUBLIC_URL}/hero.png`,
  heroGridVaillant: `${process.env.PUBLIC_URL}/hero/hero-vaillant-montaza.png`,
  heroGridVentilacija: `${process.env.PUBLIC_URL}/hero/hero-ventilacija-kanali.png`,
  heroGridRadionica: `${process.env.PUBLIC_URL}/hero/hero-radionica.png`,
  heroGridViessmann: `${process.env.PUBLIC_URL}/hero/hero-viessmann-servis.png`,
  klimatizacija: `${process.env.PUBLIC_URL}/usluge/klimatizacija.png`,
  grejanje: `${process.env.PUBLIC_URL}/usluge/grejanje.png`,
  ventilacija: `${process.env.PUBLIC_URL}/usluge/ventilacija.png`,
  vik: `${process.env.PUBLIC_URL}/usluge/vik.png`,
};

const partnerBrands = [
  {
    name: 'Vaillant',
    logo: `${process.env.PUBLIC_URL}/partners/vaillant.png`,
    logoClass: 'h-14',
  },
  {
    name: 'Midea',
    logo: `${process.env.PUBLIC_URL}/partners/midea.png`,
    logoClass: 'h-16',
  },
  {
    name: 'Gree',
    logo: `${process.env.PUBLIC_URL}/partners/gree.svg`,
    logoClass: 'h-7',
  },
  {
    name: 'Herz',
    logo: `${process.env.PUBLIC_URL}/partners/herz.png`,
    logoClass: 'h-7',
  },
];

const services = [
  {
    id: 'klimatizacija',
    title: 'Klimatizacija',
    desc: 'Profesionalna ugradnja i servis klima uređaja vrhunskog kvaliteta.',
    icon: Snowflake,
    color: 'bg-cool',
    image: IMAGES.klimatizacija,
  },
  {
    id: 'grejanje',
    title: 'Grejanje',
    desc: 'Energetski efikasni sistemi grejanja za topao dom cele godine.',
    icon: Fire,
    color: 'bg-heat',
    image: IMAGES.grejanje,
  },
  {
    id: 'ventilacija',
    title: 'Ventilacija',
    desc: 'Kvalitetna ventilacija za svež i zdrav vazduh u prostoru.',
    icon: Wind,
    color: 'bg-accent',
    image: IMAGES.ventilacija,
  },
  {
    id: 'vik',
    title: 'VIK instalacije',
    desc: 'Izrada i rekonstrukcija vodovodnih i kanalizacionih instalacija.',
    icon: Wrench,
    color: 'bg-primary',
    image: IMAGES.vik,
  },
];

const stats = [
  { number: '150+', label: 'Projekata' },
  { number: '100+', label: 'Klijenata' },
  { number: '3+', label: 'Godine' },
  { number: '24/7', label: 'Podrška' },
];

const features = [
  { icon: Lightning, title: 'Brza reakcija', desc: 'Dolazimo u najkraćem roku' },
  { icon: ShieldCheck, title: 'Garancija', desc: 'Na sve naše radove' },
  { icon: ChatCircle, title: 'Konsultacije', desc: 'Besplatni saveti' },
];

export default function HomePage() {
  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* React.createElement: CRA + CI tretira <Seo /> kao neiskorišćen import (no-unused-vars). */}
      {React.createElement(Seo, { ...SEO.home })}
      {React.createElement(JsonLdOrganization)}
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
        {/* Background - Building with AC units */}
        <div className="absolute inset-0">
          <img 
            src={IMAGES.hero}
            alt="Tehnički prostor sa ventilacionim i klima kanalima na plafonu"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary/60" />
        </div>

        {/* Floating Elements */}
        <motion.div 
          className="absolute top-1/4 right-10 w-20 h-20 bg-cool/20 rounded-full blur-xl hidden lg:block"
          animate={{ y: [0, -30, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 5, repeat: Infinity }}
        />
        <motion.div 
          className="absolute bottom-1/4 right-1/4 w-32 h-32 bg-heat/20 rounded-full blur-xl hidden lg:block"
          animate={{ y: [0, 20, 0], scale: [1, 0.9, 1] }}
          transition={{ duration: 6, repeat: Infinity }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div 
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-full mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <span className="w-2 h-2 bg-mint rounded-full animate-pulse" />
                <span className="text-white/90 text-sm font-medium">Od 2022. godine sa vama</span>
              </motion.div>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-[1.1] mb-6">
                Energija koja{' '}
                <span className="relative inline-block">
                  <span className="relative z-10 text-cool">štedi</span>
                  <motion.span 
                    className="absolute -bottom-1 left-0 w-full h-2 bg-cool/30"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                  />
                </span>
                ,<br />
                komfor koji{' '}
                <span className="text-heat">traje</span>
              </h1>

              <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-lg leading-relaxed">
                Sistemi grejanja i hlađenja za vaš dom i posao. 
                Profesionalno, brzo, pouzdano.
              </p>

              {/* CTA */}
              <div className="flex flex-wrap gap-4 mb-10">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Link
                    to={KONTAKT_FORM_PATH}
                    className="group inline-flex items-center gap-3 bg-mint hover:bg-mint/90 text-white px-6 sm:px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 shadow-lg shadow-mint/30"
                    data-testid="hero-contact-btn"
                  >
                    Javite nam se
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </motion.div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                {stats.map((stat, i) => (
                  <motion.div
                    key={i}
                    className="text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + i * 0.1 }}
                  >
                    <p className="font-heading text-2xl sm:text-3xl font-extrabold text-white">{stat.number}</p>
                    <p className="text-white/60 text-xs sm:text-sm">{stat.label}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Image Grid */}
            <motion.div
              className="hidden lg:grid grid-cols-2 gap-4"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="space-y-4">
                <motion.div
                  className="rounded-2xl overflow-hidden shadow-2xl"
                  whileHover={{ scale: 1.03 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <img
                    src={IMAGES.heroGridVaillant}
                    alt="Montaža spoljašnjih Vaillant klima uređaja na gradilištu"
                    className="w-full h-48 object-cover"
                  />
                </motion.div>
                <motion.div
                  className="rounded-2xl overflow-hidden shadow-2xl"
                  whileHover={{ scale: 1.03 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <img
                    src={IMAGES.heroGridRadionica}
                    alt="Zavarivanje metalnih cevi u radionici"
                    className="w-full h-64 object-cover"
                  />
                </motion.div>
              </div>
              <div className="space-y-4 pt-8">
                <motion.div
                  className="rounded-2xl overflow-hidden shadow-2xl"
                  whileHover={{ scale: 1.03 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <img
                    src={IMAGES.heroGridViessmann}
                    alt="Servis i ugradnja Viessmann toplotne pumpe"
                    className="w-full h-64 object-cover"
                  />
                </motion.div>
                <motion.div
                  className="rounded-2xl overflow-hidden shadow-2xl"
                  whileHover={{ scale: 1.03 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <img
                    src={IMAGES.heroGridVentilacija}
                    alt="Ugradnja ventilacionih kanala na plafonu"
                    className="w-full h-40 object-cover"
                  />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <motion.div 
              className="w-1 h-2 bg-white/60 rounded-full"
              animate={{ y: [0, 8, 0], opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* Ovlašćeni partner — čista teget, bez foto pozadine; blagi fade na ivicama */}
      <section className="relative overflow-hidden py-16 bg-primary" data-testid="partner-section">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-28 bg-gradient-to-b from-black/20 to-transparent"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-32 bg-gradient-to-t from-white via-white/40 to-transparent"
          aria-hidden
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block text-cool font-semibold uppercase tracking-[0.2em] text-sm mb-4">
              Ovlašćeni partner
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-white mb-3">
              THERMO COOL SYSTEM
            </h2>
            <p className="text-white/75">
              Ovlašćeni partner za:
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {partnerBrands.map((brand, i) => (
              <motion.div
                key={brand.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl border border-white/20 px-4 py-6 text-center shadow-lg"
                data-testid={`partner-brand-${brand.name.toLowerCase()}`}
              >
                <div className="h-16 flex items-center justify-center mb-3 px-2">
                  <img
                    src={brand.logo}
                    alt={`${brand.name} logo`}
                    className={`${brand.logoClass} w-full object-contain`}
                    loading="lazy"
                  />
                </div>
                <p className="font-heading text-lg font-bold text-primary">{brand.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white" data-testid="services-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block text-accent font-semibold uppercase tracking-[0.2em] text-sm mb-4">Naše usluge</span>
            <h2 className="font-heading text-4xl sm:text-5xl font-extrabold text-primary mb-4">
              Sistemi grejanja i hlađenja
            </h2>
            <p className="text-muted text-lg max-w-2xl mx-auto">
              Od konsultacija do ugradnje i održavanja — pružamo sve što vam je potrebno za savršen komfor.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, i) => (
              <motion.div
                key={service.id}
                className={`group card-hover ${i === 3 ? 'hidden md:block lg:hidden' : ''}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                data-testid={`service-card-${service.id}`}
              >
                <div className="bg-white rounded-3xl overflow-hidden border border-primary/5 h-full shadow-lg">
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent" />
                    <div className={`absolute top-4 left-4 w-12 h-12 ${service.color} rounded-xl flex items-center justify-center shadow-lg`}>
                      <service.icon size={24} weight="fill" className="text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="font-heading text-2xl font-bold text-primary mb-3">
                      {service.title}
                    </h3>
                    <p className="text-muted mb-6">{service.desc}</p>
                    <Link
                      to="/usluge"
                      className="inline-flex items-center gap-2 text-accent font-semibold group-hover:gap-4 transition-all duration-300"
                      data-testid={`service-link-${service.id}`}
                    >
                      Saznaj više
                      <ArrowRight size={18} weight="bold" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto mt-12 mb-6">
            <div className="bg-surface border border-primary/10 rounded-2xl px-6 py-5 text-center">
              <p className="text-primary font-semibold text-lg">
                Nudimo još mnogo drugih usluga za vaš objekat.
              </p>
              <p className="text-muted text-sm mt-1">
                Pogledajte kompletnu ponudu na stranici Usluge.
              </p>
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/usluge"
              className="inline-flex items-center gap-3 bg-primary hover:bg-secondary text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300"
              data-testid="home-more-services-btn"
            >
              Ostale usluge
              <ArrowRight size={20} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section - Why Us */}
      <section className="py-24 bg-surface" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="relative rounded-3xl overflow-hidden">
                <img
                  src={IMAGES.ventilacija}
                  alt="Ugradnja ventilacionih kanala i HVAC instalacija"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent" />
              </div>

              <motion.div
                className="absolute -bottom-8 -right-8 bg-white rounded-2xl p-6 shadow-2xl"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-mint rounded-xl flex items-center justify-center">
                    <CheckCircle size={28} weight="fill" className="text-white" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-2xl text-primary">100%</p>
                    <p className="text-muted text-sm">Zadovoljnih klijenata</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block text-accent font-semibold uppercase tracking-[0.2em] text-sm mb-4">Zašto mi</span>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mb-6">
                <img
                  src={LOGO_SRC}
                  alt="THERMO COOL SYSTEM logo"
                  className="h-16 sm:h-20 w-auto max-w-[200px] object-contain object-left flex-shrink-0"
                  data-testid="features-section-logo"
                />
                <h2 className="font-heading text-4xl sm:text-5xl font-extrabold text-primary">
                  Vaš pouzdan partner
                </h2>
              </div>
              <p className="text-muted text-lg mb-6">
                Više od 3 godine iskustva i stotine zadovoljnih klijenata potvrđuju kvalitet naših usluga.
              </p>

              {/* Gallery + kontakt */}
              <div className="bg-accent/10 rounded-2xl p-6 mb-8">
                <div className="flex items-center gap-4 mb-3">
                  <Images size={28} weight="duotone" className="text-accent" />
                  <p className="font-semibold text-primary">Pogledajte naše projekte</p>
                </div>
                <div className="flex flex-wrap gap-4">
                  <Link
                    to="/projekti"
                    className="inline-flex items-center gap-2 rounded-full border border-white/50 bg-white/70 px-4 py-2 text-sm font-semibold text-primary shadow-sm backdrop-blur-sm transition-all duration-300 hover:bg-white hover:shadow-md"
                    data-testid="gallery-cta-link"
                  >
                    Projekti
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                {features.map((feat, i) => (
                  <motion.div 
                    key={i}
                    className="flex gap-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <feat.icon size={24} weight="duotone" className="text-accent" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary mb-1">{feat.title}</h4>
                      <p className="text-muted text-sm">{feat.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative overflow-hidden py-20 bg-primary" data-testid="home-bottom-cta">
        <PrimarySectionBackdrop />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-white mb-4">
            Potrebna vam je ponuda?
          </h2>
          <p className="text-white/75 text-lg mb-8">
            Javite nam se i dobićete brz odgovor i savet za vaš projekat.
          </p>
          <Link
            to={KONTAKT_FORM_PATH}
            className="inline-flex items-center gap-3 bg-secondary hover:bg-white hover:text-primary text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300"
            data-testid="home-bottom-cta-contact"
          >
            Javite nam se
            <ArrowRight size={20} weight="bold" />
          </Link>
        </div>
      </section>

    </div>
  );
}
